import { useEffect, useRef, useState } from "react";
import di from "../../di/di";
import { isRight } from "../types/Either";
import { GetFlexPackageProductsParams } from "../../domain/useCase/GetFlexPackageProducts";
import { FlexProduct } from "../../domain/entity/FlexPackage";

export interface UseFlexPackageProductsResult {
  products: FlexProduct[];
  isLoading: boolean;
  hasFetched: boolean;
  fetchProducts: (appPid: number, packageId: string) => Promise<void>;
}

/**
 * hasFetched only flips to true on a Right result, so a failed attempt can be
 * retried on the next call instead of permanently showing an empty list.
 * inFlightRef blocks a second concurrent call while one is already pending
 * (e.g. a rapid collapse/re-expand), and mountedRef stops state updates from
 * a response that resolves after the component unmounts.
 */
export function useFlexPackageProducts(): UseFlexPackageProductsResult {
  const [products, setProducts] = useState<FlexProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function fetchProducts(appPid: number, packageId: string) {
    if (inFlightRef.current || hasFetched) {
      return;
    }
    inFlightRef.current = true;
    setIsLoading(true);
    const result = await di.getFlexPackageProducts.execute(
      new GetFlexPackageProductsParams(appPid, packageId)
    );
    inFlightRef.current = false;
    if (!mountedRef.current) {
      return;
    }
    if (isRight(result)) {
      setProducts(result.value);
      setHasFetched(true);
    }
    setIsLoading(false);
  }

  return { products, isLoading, hasFetched, fetchProducts };
}
